import { ReactNode } from "react";
import { Box, IconButton, Slider, SxProps, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { useAudioPlayer } from "@components/shared/hooks/useAudioPlayer";

interface AudioPlayerBarProps {
  src: string;
  initialDuration?: number;
  label?: ReactNode;
  playButtonSx?: SxProps;
  sliderSx?: SxProps;
  timeLabelSx?: SxProps;
}

export function formatAudioTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${mins}:${secs}`;
}

export function AudioPlayerBar({ src, initialDuration = 0, label, playButtonSx, sliderSx, timeLabelSx }: AudioPlayerBarProps) {
  const { isPlaying, currentTime, duration, togglePlay, seek } = useAudioPlayer(src, initialDuration);

  const progress = duration ? (currentTime / duration) * 100 : 0;

  const handleSliderChange = (_event: Event, value: number | number[]) => {
    if (!duration) return;
    seek(((value as number) / 100) * duration);
  };

  return (
    <Box display="flex" alignItems="center" gap={1.5}>
      <IconButton size="small" onClick={togglePlay} sx={playButtonSx}>
        {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
      </IconButton>

      <Box flex={1} minWidth={0}>
        {label}

        <Slider value={progress} min={0} max={100} onChange={handleSliderChange} disabled={!duration} sx={sliderSx} />

        <Box display="flex" justifyContent="space-between" mt={0.2}>
          <Typography variant="caption" sx={timeLabelSx}>
            {formatAudioTime(currentTime)}
          </Typography>

          <Typography variant="caption" sx={timeLabelSx}>
            {formatAudioTime(duration || initialDuration)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
